import { forwardRef, HTMLProps, ReactNode } from 'react';

import Link, { LinkProps } from 'next/link';
import { tv } from 'tailwind-variants';

import cn from '@/helpers/utils/classNames';

export interface RawButtonLinkProps {
  scheme: 'primary' | 'secondary';
  variant: 'outlined' | 'filled';
  children: ReactNode;
  href: string;
  type?: Pick<HTMLProps<HTMLAnchorElement>, 'type'>;
  className?: Pick<HTMLProps<HTMLAnchorElement>, 'className'>;
  disabled?: Pick<HTMLProps<HTMLAnchorElement>, 'disabled'>;
}

type ButtonLinkProps = RawButtonLinkProps &
  (
    | ({
        as?: 'Link';
      } & Omit<LinkProps, 'href' | 'ref'>)
    | ({
        as?: 'a';
        // href: string;
      } & Omit<HTMLProps<HTMLAnchorElement>, 'href' | 'ref'>)
  );

export const buttonStyles = tv({
  base: 'border whitespace-nowrap transition-colors p-1 px-4 text-base hover:transition-colors rounded-full',
  variants: {
    primary: {
      filled:
        'border-transparent text-white bg-primary outline-grayscale-40 hover:text-primary hover:border-primary hover:bg-white active:shadow-button active:bg-none active:outline-0',
      outlined: 'bg-white text-primary border-primary active:shadow-button active:bg-none active:outline-0',
    },
    secondary: {
      filled:
        'border-transparent text-white bg-grayscale-40 hover:text-grayscale-40 outline-primary hover:border-grayscale-40 hover:bg-transparent',
      outlined: 'bg-transparent text-grayscale-70 border-grayscale-30 hover:text-primary',
    },
  },
});

const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(function ButtonLink(props, ref) {
  const { as, scheme, type, variant, disabled, className, href = '#', children, ...restProps } = props;

  const Comp = as === 'a' ? 'a' : Link;

  return (
    <Comp
      ref={ref}
      href={href}
      className={cn(
        buttonStyles({ [scheme]: variant }),
        {
          'cursor-not-allowed border-transparent bg-grayscale-60 text-white hover:border-transparent hover:bg-grayscale-70 hover:text-white focus:bg-grayscale-70 focus:text-white':
            disabled,
        },
        className
      )}
      {...restProps}
    >
      {children}
    </Comp>
  );
});

export default ButtonLink;
